/**
 * RecommendationDetail — right-hand panel showing full data for a selected bucket
 */
import type { PricingRecommendation, ConfidenceDriver } from '../../types/pricingIntelligence';
import { ACTION_STATUS_ROLE, type StatusRole } from '../../config/colors';

interface Props {
  recommendation: PricingRecommendation | null;
}

// Role → plain-pill class shape (this panel's expression). The action → role
// assignment itself lives in config/colors.ts (ACTION_STATUS_ROLE) so it is
// defined exactly once.
const ROLE_PILL: Record<StatusRole | 'neutral', string> = {
  success: 'bg-success/60 text-success',
  destructive: 'bg-destructive/60 text-destructive',
  warning: 'bg-warning/60 text-warning',
  info: 'bg-ring/60 text-ring',
  neutral: 'bg-secondary text-muted-foreground',
};

function Bar({ value, max = 1 }: { value: number; max?: number }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
      <div className="h-full rounded-full bg-success/70" style={{ width: `${pct}%` }} />
    </div>
  );
}

function SignalRow({ label, value }: { label: string; value: number }) {
  const pct = Math.min(100, Math.max(0, (value + 1) * 50)); // -1..+1 → 0..100
  const colour =
    value > 0.1 ? 'bg-success/60' : value < -0.1 ? 'bg-destructive/60' : 'bg-warning/60';
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground text-xs w-36 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-muted-foreground text-xs w-12 text-right font-mono">
        {value >= 0 ? '+' : ''}{value.toFixed(3)}
      </span>
    </div>
  );
}

function DriverBadge({ driver }: { driver: ConfidenceDriver }) {
  // `source` is a provenance tier (Constitution §13.2) — an inference is not a
  // confirmation, so inferred wears the violet provenance token, not amber.
  const sourceColour: Record<string, string> = {
    observed: 'text-provenance-observed',
    declared: 'text-provenance-declared',
    inferred: 'text-provenance-inferred',
    assumed: 'text-muted-foreground',
  };
  return (
    <div className="bg-card rounded px-2 py-1.5 text-xs space-y-0.5">
      <div className="flex justify-between items-center">
        <span className="text-foreground font-medium">{driver.signal}</span>
        <span className={`text-xs ${sourceColour[driver.source] ?? 'text-muted-foreground'}`}>
          {driver.source}
        </span>
      </div>
      <div className="flex gap-2 text-muted-foreground">
        <span>w={driver.weight.toFixed(2)}</span>
        <span>Δ={driver.contribution >= 0 ? '+' : ''}{driver.contribution.toFixed(3)}</span>
      </div>
      {driver.note && <p className="text-muted-foreground text-xs leading-tight">{driver.note}</p>}
    </div>
  );
}

export default function RecommendationDetail({ recommendation: rec }: Props) {
  if (!rec) {
    return (
      <div className="flex items-center justify-center h-full min-h-50">
        <p className="text-sm text-muted-foreground">Select a bucket to view details</p>
      </div>
    );
  }

  const { signals } = rec;
  const dc = signals.dataCompleteness;

  return (
    <div className="space-y-4 text-sm">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-foreground font-semibold">{rec.regionId}</span>
            <span className="text-muted-foreground mx-1">·</span>
            <span className="text-foreground">{rec.vehicleClass}</span>
          </div>
          <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${ROLE_PILL[ACTION_STATUS_ROLE[rec.recommendedAction]]}`}>
            {rec.recommendedAction.toUpperCase()} {rec.recommendedAdjustmentPct >= 0 ? '+' : ''}{rec.recommendedAdjustmentPct.toFixed(1)}%
          </span>
        </div>

        <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
          <span>composite {rec.compositeScore.toFixed(3)}</span>
          <span>confidence {(rec.confidence * 100).toFixed(0)}%</span>
        </div>
      </div>

      {/* Manager summary */}
      {rec.managerHeadline && (
        <div className="bg-card/60 rounded-lg px-3 py-2.5 space-y-1">
          <p className="text-foreground text-sm font-medium">{rec.managerHeadline}</p>
          <p className="text-muted-foreground text-xs">{rec.adjustmentRange}</p>
          <p className="text-muted-foreground text-xs">{rec.whyNow}</p>
          <p className="text-muted-foreground text-xs italic">{rec.whatThisMeans}</p>
        </div>
      )}

      {/* Review warning */}
      {rec.reviewRequired && (
        <div className="bg-warning/20 border-warning/40 rounded-lg px-3 py-2 text-warning text-xs flex gap-2">
          <span>⚠</span>
          <span>Human review recommended — data quality flags raised.</span>
        </div>
      )}

      {/* Reasoning */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Reasoning</h3>
        <ul className="space-y-1">
          {rec.reasoning.map((line, i) => (
            <li key={i} className="text-muted-foreground text-xs flex gap-1.5">
              <span className="text-muted-foreground shrink-0">·</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Signals */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Signals</h3>
        <div className="space-y-1.5">
          <SignalRow label="Demand pressure"        value={signals.demandPressure} />
          <SignalRow label="Capacity pressure"      value={signals.capacityPressure} />
          <SignalRow label="Idle risk"               value={-signals.idleRisk} />
          <SignalRow label="Reposition penalty"      value={-signals.repositionPenalty} />
          <SignalRow label="Position value"          value={signals.positionValue} />
          <SignalRow label="Strategic adjustment"    value={signals.strategicAdjustment} />
          <SignalRow label="Realisation signal"      value={signals.realisationSignal} />
          {signals.marginSignal !== null && (
            <SignalRow label="Margin signal"         value={signals.marginSignal} />
          )}
        </div>
      </div>

      {/* Data completeness */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Data Completeness</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {(['gps', 'orders', 'fuel', 'drivers'] as const).map(field => (
            <div key={field}>
              <div className="flex justify-between mb-0.5">
                <span className="text-muted-foreground text-xs capitalize">{field}</span>
                <span className="text-muted-foreground text-xs">{(dc[field] * 100).toFixed(0)}%</span>
              </div>
              <Bar value={dc[field]} />
            </div>
          ))}
        </div>
      </div>

      {/* Confidence drivers */}
      {signals.confidenceDrivers.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Confidence Drivers</h3>
          <div className="space-y-1.5">
            {signals.confidenceDrivers.map((d, i) => (
              <DriverBadge key={i} driver={d} />
            ))}
          </div>
        </div>
      )}

      {rec.notes && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Notes</h3>
          <p className="text-muted-foreground text-xs">{rec.notes}</p>
        </div>
      )}
    </div>
  );
}
